import { useMemo } from "react";
import { useAppSelector } from "../../framework/redux/hooks";
import {
    makeSelectWorkShiftTimeSlotsByIds,
    workShiftTimeSlotSelector,
} from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { Base } from "../../framework/common/base";
import { WorkShift } from "../../models/workShift/workShift";
import { IWorkShiftTimeSlot } from "../../models/workShiftTimeSlot/workShiftTimeSlot";
import { useSelectWorkShiftsByDate } from "./useSelectWorkShifts";

export const useSelectWorkShiftTimeSlotById = (id: string) => {
    const state = useAppSelector((state) => state.workShiftTimeSlot);
    return workShiftTimeSlotSelector.selectById(state, id);
};

export const useSelectWorkShiftTimeSlotByIds = (ids: string[]) => {
    const state = useAppSelector((state) => state.workShiftTimeSlot);
    const selectWorkShiftTimeSlotsByIds = useMemo(
        makeSelectWorkShiftTimeSlotsByIds,
        []
    );
    return selectWorkShiftTimeSlotsByIds(state, ids);
};

export const useSelectCurrentWorkShiftTimeSlot = () => {
    const currentId = useAppSelector(
        (state) => state.workShiftTimeSlot.currentId
    );
    return useSelectWorkShiftTimeSlotById(currentId);
};

export const useFilterWorkShiftTimeSlotsByPlannedShiftStart = (
    rangeStart: string,
    rangeEnd: string
) => {
    const shifts = useSelectWorkShiftsByDate(rangeStart, rangeEnd);
    const slots = useSelectWorkShiftTimeSlotByIds(
        shifts?.flatMap((s) => s.workShiftTimeSlotIds)
    );

    return useMemo(() => {
        const returnSlots: IWorkShiftTimeSlot[] = [];

        if (!slots?.length) return returnSlots;

        const slotToShift = new Map<string, WorkShift>();
        for (const shift of shifts) {
            for (const id of shift?.workShiftTimeSlotIds ?? []) {
                slotToShift.set(id, shift);
            }
        }

        slots.forEach((slot) => {
            const shift = slotToShift.get(slot.id);
            const plannedStart =
                shift?.startTimePlannedWorkShiftId && shift?.startTime
                    ? shift.startTime
                    : undefined;
            if (!plannedStart) {
                returnSlots.push(slot);
            } else if (Base.isAfter(slot.endDate, plannedStart, "minute")) {
                returnSlots.push({
                    ...slot,
                    // Slot start is set to planned shift start if it started earlier.
                    startDate: Base.dateToJsonDateTimeWithOffset(
                        Base.maxDate([slot.startDate, plannedStart])
                    ),
                });
            }
        });

        return returnSlots;
    }, [slots, shifts]);
};